import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { useMunicipality } from "@/context/MunicipalityContext";
import { formatMunicipalityLabel } from "@/lib/municipalityLabel";
import { createSubmission, type Submission, type SubmissionPlanType } from "@/api/submissions";

const PLAN_TYPE_OPTIONS: Array<{ value: SubmissionPlanType; label: string }> = [
  { value: "residential_site_plan", label: "Residential site plan" },
  { value: "lot_split", label: "Lot split" },
  { value: "lot_combination", label: "Lot combination" },
];

type SubmissionIntakeDialogProps = {
  onCreated?: (submission: Submission) => void;
};

export function SubmissionIntakeDialog({ onCreated }: SubmissionIntakeDialogProps) {
  const { municipality } = useMunicipality();

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [planType, setPlanType] = useState<SubmissionPlanType>("residential_site_plan");
  const [applicantName, setApplicantName] = useState("");
  const [projectName, setProjectName] = useState("");
  const [applicantEmail, setApplicantEmail] = useState("");
  const [description, setDescription] = useState("");
  const [desiredZoning, setDesiredZoning] = useState("");
  const [siteAddress, setSiteAddress] = useState("");
  const [parcelIdentifier, setParcelIdentifier] = useState("");

  const resetForm = () => {
    setFile(null);
    setPlanType("residential_site_plan");
    setApplicantName("");
    setProjectName("");
    setApplicantEmail("");
    setDescription("");
    setDesiredZoning("");
    setSiteAddress("");
    setParcelIdentifier("");
    setError(null);
  };

  const canSubmit =
    !!municipality?.id && !!file && !!applicantName.trim() && !!projectName.trim() && !saving;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) resetForm();
  };

  const handleSubmit = async () => {
    if (!municipality?.id || !file) return;
    setSaving(true);
    setError(null);
    try {
      const { submission } = await createSubmission(municipality.id, {
        file,
        plan_type: planType,
        applicant_name: applicantName,
        project_name: projectName,
        applicant_email: applicantEmail,
        description,
        desired_zoning: desiredZoning,
        site_address: siteAddress,
        parcel_identifier: parcelIdentifier,
      });
      onCreated?.(submission);
      setOpen(false);
      resetForm();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create submission");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>Log a submission</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Log a submission</DialogTitle>
          <DialogDescription>
            Intake a site plan submission for {formatMunicipalityLabel(municipality) || "this municipality"}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!!error && (
            <Card className="border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</Card>
          )}

          <div className="space-y-2">
            <Label htmlFor="submission-file">Plan PDF</Label>
            <Input
              id="submission-file"
              type="file"
              accept=".pdf"
              onChange={(event) => setFile(event.target.files?.[0] || null)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="submission-plan-type">Plan type</Label>
            <Select value={planType} onValueChange={(value) => setPlanType(value as SubmissionPlanType)}>
              <SelectTrigger id="submission-plan-type">
                <SelectValue placeholder="Select plan type" />
              </SelectTrigger>
              <SelectContent>
                {PLAN_TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="submission-applicant-name">Applicant name</Label>
              <Input
                id="submission-applicant-name"
                value={applicantName}
                onChange={(event) => setApplicantName(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="submission-applicant-email">Applicant email</Label>
              <Input
                id="submission-applicant-email"
                type="email"
                value={applicantEmail}
                onChange={(event) => setApplicantEmail(event.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="submission-project-name">Project name</Label>
            <Input
              id="submission-project-name"
              value={projectName}
              onChange={(event) => setProjectName(event.target.value)}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="submission-site-address">Site address</Label>
              <Input
                id="submission-site-address"
                value={siteAddress}
                onChange={(event) => setSiteAddress(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="submission-parcel-identifier">Parcel identifier</Label>
              <Input
                id="submission-parcel-identifier"
                value={parcelIdentifier}
                onChange={(event) => setParcelIdentifier(event.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="submission-desired-zoning">Desired zoning</Label>
            <Input
              id="submission-desired-zoning"
              value={desiredZoning}
              onChange={(event) => setDesiredZoning(event.target.value)}
              placeholder="e.g. R-3, C-2"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="submission-description">Description</Label>
            <Textarea
              id="submission-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {saving ? "Submitting…" : "Create submission"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
